import React, { useContext, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  Menu,
  Badge,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  GitHub as GitHubIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import Profile from "./Profile.tsx";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import Notification from "./Notification.tsx";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { PageDataContext } from "../../utils/common/PageDataContext.tsx";
import { useSelector } from "react-redux";

// Types and Interfaces
interface User {
  USER_PARAM?: {
    USERNAME?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface RootState {
  user: User;
}

interface PageData {
  title: string;
  [key: string]: any;
}

interface PageDataContextType {
  pageData: PageData;
  setPageData?: (data: PageData) => void;
}

interface HeaderProps {
  handleDrawerToggle?: () => void;
  handleDesktopToggle: () => void;
  desktopOpen: boolean;
  pageTitle?: string;
}

// Extended theme interface
interface CustomTheme {
  palette: {
    text: {
      primary: string;
      secondary?: string;
    };
    background: {
      default: string;
    };
  };
  typography: {
    subtitle_s?: {
      fontSize: string;
    };
  };
}

const Header: React.FC<HeaderProps> = ({
  handleDrawerToggle,
  handleDesktopToggle,
  desktopOpen,
  pageTitle,
}) => {
  const theme = useTheme() as CustomTheme;
  const styles = useStyles(theme);
  const location = useLocation();

  // Get user parameters from Redux store
  const SYS_USER: User = useSelector((state: RootState) => state.user) || {};
  const { USERNAME } = SYS_USER.USER_PARAM || {};

  const selectedPage = location.pathname.replace("/", "") || "Dashboard";
  const { pageData } = useContext(PageDataContext) as PageDataContextType;
  
  // State for profile dropdown menu
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const profileOpen = Boolean(profileAnchorEl);

  // State for notification dropdown menu
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const notificationOpen = Boolean(notificationAnchorEl);

  // State for selected location
  const [selectedLocation, setSelectedLocation] = useState<string>("Location 1");

  // Handle profile menu open/close
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setProfileAnchorEl(event.currentTarget);
  };
  
  const handleProfileMenuClose = (): void => {
    setProfileAnchorEl(null);
  };

  // Handle notification menu open/close
  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setNotificationAnchorEl(event.currentTarget);
  };
  
  const handleNotificationMenuClose = (): void => {
    setNotificationAnchorEl(null);
  };

  // Handle location change
  const handleLocationChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
    setSelectedLocation(event.target.value as string);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        ...styles.header,
        width: { sm: `calc(100% - ${desktopOpen ? 240 : 0}px)` },
      }}
    >
      <Toolbar>
        <IconButton
          aria-label="toggle drawer"
          edge="start"
          onClick={handleDesktopToggle}
          sx={styles.headerHamburgerIcon}
        >
          <MenuOpenIcon />
        </IconButton>
        {/*page title  */}
        <Box sx={styles.title_container}>
          <Typography sx={styles.title_text}>{pageData.title}</Typography>
        </Box>
        
        <Box sx={{ flexGrow: 1 }} />

        <Box display={"flex"} gap={2} alignContent={"center"}>
          <IconButton onClick={handleNotificationMenuOpen}>
            <Badge badgeContent={100} color="primary">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Menu
            anchorEl={notificationAnchorEl}
            open={notificationOpen}
            onClose={handleNotificationMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={styles.headerNotificationMenu}
            MenuListProps={{
              sx: {
                paddingTop: 0,
                paddingBottom: 0,
                boxShadow: 1,
                padding: "1px",
                backgroundColor: "transparent",
              },
            }}
          >
            <Notification />
          </Menu>

          <Box sx={styles.boxHeaderProfile} onClick={handleProfileMenuOpen}>
            <Avatar
              alt="John Doe"
              src="https://storage.googleapis.com/a1aa/image/sEOZWreQ55qwQmRvi1gGJdVCTxq404PM_Rpw9wDBMug.jpg"
              sx={styles.headerImage}
            />
            <Typography
              sx={{
                fontSize: theme.typography.subtitle_s?.fontSize || '0.875rem',
                color: theme.palette.text.primary,
              }}
            >
              {USERNAME || 'User'}
            </Typography>
          </Box>

          <Menu
            anchorEl={profileAnchorEl}
            open={profileOpen}
            onClose={handleProfileMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={styles.headerProfileMenu}
            MenuListProps={{
              sx: {
                paddingTop: 0,
                paddingBottom: 0,
                boxShadow: 10,
                padding: "1px",
                backgroundColor: "transparent",
              },
            }}
          >
            <Profile />
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;